import * as React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ShiftManagementPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Form from 'modules/shift_management/components/Settings/SavedShifts/Form'
import { PermissionsProvider } from 'modules/shift_management/hooks/usePermissions'
import buildShiftsVisibility from 'modules/shift_management/lib/buildShiftsVisibility'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackAddDefaultShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'

const NewSavedShiftComponent = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { bulkCud } = useBulkCudPlannedBreaks()
  const [plannedBreakForms, setPlannedBreakForms] = useState<PlannedBreakForm[]>([])
  const { data: permissions, loading, error } = useQuery(ShiftManagementPageDocument, {})

  const form = useLazyRef(() => new DefaultShiftForm()).current
  const onSubmit = async () => {
    trackAddDefaultShiftSubmit({
      name: form.get('title').value,
      color: nameFromHexCode(form.get('color').value),
      startAt: form.get('start_at').value,
      endAt: form.get('end_at').value,
      workplace: form.get('location_id').value,
      workarea: form.get('work_area_id').value,
      comments: form.get('notes').value,
      allCompany: !!form.get('all_company').value,
      teamsIdsVisibilities: form.get('teams_ids_visibilities').value,
      locationsIdsVisibilities: form.get('locations_ids_visibilities').value,
      from: 'settings',
    })

    const { teamsIdsVisibilities, locationsIdsVisibilities } = buildShiftsVisibility({
      form: form,
      isSingleSelector: false,
    })

    const result = await asyncAction(
      () =>
        defaultShiftsCollection.create({
          title: form.get('title').value,
          color: form.get('color').value ? form.get('color').value : null,
          start_at: form.get('start_at').value,
          end_at: form.get('end_at').value,
          location_id: form.get('location_id').value,
          work_area_id: form.get('work_area_id').value,
          notes: form.get('notes').value,
          all_company: !!form.get('all_company').value,
          teams_ids_visibilities: teamsIdsVisibilities,
          locations_ids_visibilities: locationsIdsVisibilities,
          extra_hours: !!form.get('extra_hours').value,
        }),
      {
        successMessage: i18n.t('shift_management.saved_shifts.actions.create.success'),
      }
    )
    if (isSuccessful(result)) {
      await asyncAction(() =>
        bulkCud({
          plannedBreaksParams: [
            {
              defaultShiftId: result.value.id.toString(),
              plannedBreaks: plannedBreakForms.map((pb) => ({
                startAt: pb.get('start_at').value,
                endAt: pb.get('end_at').value,
                duration: +pb.get('duration').value,
                breakType: pb.get('break_type').value,
                breakConfigurationId: pb.get('break_configuration.id').value,
              })),
            },
          ],
        })
      )
    }

    navigate(navigation.shifts.savedShifts.fullPath())
  }

  return (
    <PermissionsProvider permissions={permissions}>
      <ResponsivePage.Body loading={loading} error={error}>
        <Form
          form={form}
          plannedBreakForms={plannedBreakForms}
          setPlannedBreakForms={setPlannedBreakForms}
          onSubmit={onSubmit}
        />
      </ResponsivePage.Body>
    </PermissionsProvider>
  )
}

const NewSavedShift = observer(NewSavedShiftComponent)

export default NewSavedShift
